import { ParticipantTile, useTracks } from '@livekit/components-react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Crown, Expand, Hand, Pin } from 'lucide-react';
import { useLiveKit } from '../contexts';
import { updateMeeting } from '@/modules/meetings/api/api-meeting';
import { resolveLayout } from '@/modules/meetings/utils/layout';
import { toast } from 'sonner';
import { ControlledParticipantTile, useParticipantMicrophoneControl } from './ParticipantMicrophoneControl';

interface VideoConferenceProps {
  room: Room;
  mainScreenFocus: string | null;
  mainScreenType: 'camera' | 'screen_share' | null;
  onMainScreenFocusChange: (identity: string | null) => void;
}
export function VideoConference({room, mainScreenFocus, mainScreenType, onMainScreenFocusChange}: VideoConferenceProps) {
  const { meeting, isHostOrCoHost, refreshMeeting, cameraResolutionPolicy } = useLiveKit();
  const microphoneControls = useParticipantMicrophoneControl();
  const [,rerender]=useState(0);
  const [pinning,setPinning]=useState(false);
  const tracks=useTracks([{source:Track.Source.Camera,withPlaceholder:true},{source:Track.Source.ScreenShare,withPlaceholder:false}],{onlySubscribed:false});
  useEffect(()=>{
    const update=()=>rerender(v=>v+1);
    const events=[RoomEvent.TrackMuted,RoomEvent.TrackUnmuted,RoomEvent.TrackPublished,RoomEvent.TrackUnpublished,
      RoomEvent.LocalTrackPublished,RoomEvent.LocalTrackUnpublished,RoomEvent.ParticipantConnected,RoomEvent.ParticipantDisconnected];
    events.forEach(e=>room.on(e,update));
    return ()=>{events.forEach(e=>room.off(e,update));};
  },[room]);
  const cameras=tracks.filter(t=>t.source===Track.Source.Camera);
  const screens=tracks.filter(t=>t.source===Track.Source.ScreenShare).sort((a,b)=>a.participant.identity<b.participant.identity?-1:1);
  const result=resolveLayout(cameraResolutionPolicy?.layout ?? meeting?.layout ?? 'custom', cameraResolutionPolicy?.automatic ?? meeting?.autoLayoutByMic ?? false,
    cameras.map(track=>({identity:track.participant.identity,micOn:track.participant.isMicrophoneEnabled,track})));
  const visible=result.participants.map(p=>p.track);
  const host=visible.find(t=>meeting?.participants.some(p=>p.identity===t.participant.identity && p.role==='host'));
  const pinned=(mainScreenType==='screen_share'?screens:cameras).find(t=>t.participant.identity===mainScreenFocus);
  const serverMain = cameraResolutionPolicy && (cameraResolutionPolicy.mainSource === 'screen_share' ? screens : cameras).find(t => t.participant.identity === cameraResolutionPolicy.mainIdentity);
  const main=serverMain || (result.automatic ? result.presenters[0]?.track ?? host ?? visible[0] : pinned ?? screens[0] ?? host ?? visible[0]);
  if (cameraResolutionPolicy) {
    result.presenters = result.participants.filter(p => cameraResolutionPolicy.mainCameraIdentities.includes(p.identity));
  }
  const presenters=result.automatic ? (result.presenters.length ? result.presenters.map(p=>p.track) : main ? [main] : []) : [];
  const audience=visible.filter(t=>!presenters.includes(t)).sort((a,b)=>{
    const raised=(id:string)=>meeting?.participants.some(p=>(p.identity===id || p.userId===id) && p.isRaiseHand)?1:0;
    return raised(b.participant.identity)-raised(a.participant.identity);
  });
  const strip=[...screens,...visible].filter(t=>t!==main);
  const pin=async(track:typeof tracks[number])=>{
    if(!meeting || !isHostOrCoHost || pinning)return;
    const unpin=mainScreenFocus===track.participant.identity && mainScreenType===(track.source===Track.Source.ScreenShare?'screen_share':'camera');
    setPinning(true);
    try{
      await updateMeeting(meeting.id,{focusParticipant:unpin?'':track.participant.identity,mainType:track.source===Track.Source.ScreenShare?'screen_share':'camera'});
      onMainScreenFocusChange(unpin?null:track.participant.identity);await refreshMeeting();
    }catch{toast.error('Không cập nhật được màn hình chính');}finally{setPinning(false);}
  };
  const tile=(track:typeof tracks[number])=>{
    const participant=meeting?.participants.find(p=>p.identity===track.participant.identity || p.userId===track.participant.identity);
    return <div key={`${track.participant.identity}-${track.source}`} className="relative h-full min-h-0 min-w-0 overflow-hidden rounded-lg bg-[#242830]">
      {isHostOrCoHost
        ? <ControlledParticipantTile trackRef={track} controls={microphoneControls}/>
        : <ParticipantTile trackRef={track} className="h-full w-full"/>}
      <div className="absolute top-2 left-2 flex gap-1 pointer-events-none">
        {(participant?.role==='host' || participant?.isCoHost || participant?.userId===meeting?.createdBy) && <span className="flex gap-1 rounded bg-purple-700 px-2 py-1 text-xs text-white"><Crown className="size-3"/>{participant?.role==='host'?'Chủ trì':'Đồng chủ trì'}</span>}
        {participant?.isRaiseHand && <Hand className="size-5 text-emerald-400"/>}
      </div>
      {isHostOrCoHost && <Button type="button" disabled={pinning} size="icon" variant="secondary" className="absolute top-2 right-2 h-7 w-7" aria-label="Chọn làm màn hình chính" onClick={()=>pin(track)}><Pin className="size-3"/></Button>}
    </div>;
  };
  const gridTracks=visible.map(t=>screens.find(s=>s.participant.identity===t.participant.identity) ?? t);
  const columns=Math.max(1,Math.ceil(Math.sqrt(gridTracks.length)));
  return <div className="relative h-full min-h-0 w-full p-2 text-white" data-layout={result.layout}>
    {!main ? <p>Đang chờ người tham gia…</p> : result.automatic ?
      <div className="flex h-full min-h-0 gap-2" data-auto-mic="true">
        {audience.length>0 && <div className="order-last flex w-[22%] min-w-24 max-w-64 shrink-0 flex-col gap-2 overflow-y-auto" aria-label="Thành viên khác và trạng thái giơ tay">
          {audience.map(t=><div key={t.participant.identity} className="aspect-video shrink-0">{tile(t)}</div>)}
        </div>}
        <div className="grid min-h-0 min-w-0 flex-1 gap-2" aria-label="Người thuyết trình" style={{
          gridTemplateColumns:`repeat(${Math.ceil(Math.sqrt(presenters.length))},minmax(0,1fr))`,
          gridTemplateRows:`repeat(${Math.ceil(presenters.length/Math.ceil(Math.sqrt(presenters.length)))},minmax(0,1fr))`
        }}>{presenters.map(tile)}</div>
      </div> : result.layout==='grid' ?
      <div className="grid h-full gap-2" style={{gridTemplateColumns:`repeat(${columns},minmax(0,1fr))`,gridTemplateRows:`repeat(${Math.ceil(gridTracks.length/columns)},minmax(0,1fr))`}}>{gridTracks.map(tile)}</div> :
      <div className={`flex h-full gap-2 ${result.layout==='smart'?'flex-col':'flex-row'}`}>
        {result.layout==='smart' && strip.length>0 && <div className="flex h-[22%] min-h-16 gap-2 overflow-x-auto">{strip.map(t=><div key={`${t.participant.identity}-${t.source}`} className="h-full aspect-video shrink-0">{tile(t)}</div>)}</div>}
        <div className="min-h-0 min-w-0 flex-1">{tile(main)}</div>
        {result.layout==='custom' && strip.length>0 && <div className="flex w-[22%] min-w-20 max-w-64 flex-col gap-2 overflow-y-auto">{strip.map(t=><div key={`${t.participant.identity}-${t.source}`} className="aspect-video shrink-0">{tile(t)}</div>)}</div>}
      </div>}
    <Button type="button" variant="secondary" size="icon" className="absolute bottom-3 right-3" aria-label="Toàn màn hình" onClick={e=>{
      const stage=e.currentTarget.parentElement;
      if(document.fullscreenElement)void document.exitFullscreen();else void stage?.requestFullscreen?.();
    }}><Expand className="size-4"/></Button>
  </div>;
}
